import { useState } from "react";
import { Link } from "react-router-dom";

// ─── Legal / trust pages ───────────────────────────────────────────
//
// Why this exists: the master plan targets paid Stripe tiers + runs
// analytics. A privacy policy is effectively mandatory once money and
// analytics are involved; "no contact / no about" hurts trust and
// YMYL-adjacent SEO. Currently entirely absent — this closes that.
//
// Voice: lean, plain-English, lightly human but SOBER (no jokes inside
// a privacy policy — the honesty is the brand, not gags). The footer
// link that reaches this is the flippant part ("The boring but
// necessary"); the pages themselves are straight.
//
// ⚠️ DRAFT — NOT LEGALLY VETTED. This is a genuine-coverage starting
// draft (covers the standard bases far better than the current state,
// which is nothing). It MUST be reviewed by someone who does this for
// real BEFORE the paid Stripe tiers go live. Do not treat as final.

const UPDATED = "May 2026";

const TABS = [
  { key: "privacy", label: "Privacy" },
  { key: "terms", label: "Terms" },
  { key: "accuracy", label: "Accuracy" },
  { key: "about", label: "About" },
  { key: "contact", label: "Contact" },
];

const P = ({ children }) => (
  <p style={{ fontSize: 14, color: "#C8CADF", lineHeight: 1.7, margin: "0 0 14px" }}>{children}</p>
);
const H = ({ children }) => (
  <h2 style={{ fontSize: 16, fontWeight: 700, color: "#E8E9F3", margin: "26px 0 10px" }}>{children}</h2>
);

function Privacy() {
  return (
    <>
      <P><strong>Short version:</strong> the free tools run entirely in your browser. We don't make you sign in, we don't sell data, and we collect as little as possible. The longer version is below because once there's any payment or analytics involved, "trust us" isn't good enough.</P>
      <H>What we collect</H>
      <P>Free tools: no account, no personal data required to use them. Basic, privacy-respecting analytics (page views, rough region, device type) help us see which tools are used and whether something is broken. No selling, no ad-tracking profiles.</P>
      <P>If and when paid features launch: payment is handled by a third-party processor (Stripe). We never see or store full card numbers. We would store a minimal record that a purchase happened (so you get what you paid for).</P>
      <H>What we don't do</H>
      <P>We don't sell or rent your data. We don't run third-party ad trackers. We don't require an account to use the free tools. We don't email you unless you contact us first.</P>
      <H>Your choices</H>
      <P>You can use every free tool without identifying yourself. You can block analytics with any standard content blocker and the tools still work. For questions about data, use the Contact tab.</P>
      <H>Changes</H>
      <P>If this policy materially changes, the "last updated" date changes. Continued use after a change means you accept the updated policy.</P>
    </>
  );
}

function Terms() {
  return (
    <>
      <P><strong>Short version:</strong> these are free study tools provided as-is to help you check your work. They're not a substitute for learning the material, your instructor, or professional advice. Use them, but verify anything that matters.</P>
      <H>Use of the tools</H>
      <P>NerdSuite is provided for educational and personal use. You may use the tools for schoolwork, studying, and general reference. Don't abuse the service (automated scraping, attempts to disrupt it, etc.).</P>
      <H>No warranty</H>
      <P>The tools are provided "as is," without warranty of any kind. We work to keep them accurate, but we don't guarantee every result is correct for every input. You're responsible for checking results you rely on — especially for graded work.</P>
      <H>Limitation of liability</H>
      <P>To the extent permitted by law, NerdSuite is not liable for losses arising from use of the tools, including incorrect results or unavailability. If you find a wrong answer, tell us — we treat correctness bugs as high priority.</P>
      <H>Paid features</H>
      <P>If paid features launch, their specific terms (billing, refunds) will be stated at the point of purchase and added here.</P>
    </>
  );
}

function Accuracy() {
  return (
    <>
      <P><strong>Built for checking work, not replacing instruction.</strong> NerdSuite tools are designed to be accurate and to show their working where it helps. But they are a study aid, not an authority.</P>
      <P>Validate anything that affects a grade against your textbook, notes, or instructor. Where a result is ambiguous or an input is malformed, tools aim to say so rather than guess — but no automated tool is perfect.</P>
      <P>The Anatomy reference and any science content are general educational material, not medical, clinical, or professional guidance. Found something wrong? The Contact tab — correctness reports are the highest-priority kind of feedback we get.</P>
    </>
  );
}

function About() {
  return (
    <>
      <P>NerdSuite is a free set of study tools for students — chemistry, math, physics, biology, and everyday utilities. The idea is simple: fast, honest tools that don't make you sign up, don't show ads, and don't treat you as the product.</P>
      <P>It's built and maintained by a small independent effort, not a venture-backed company with a sales team. Most of it is free and intended to stay that way; if AI-powered features arrive, they'll be the part that has real per-use costs, and that will be stated plainly.</P>
      <P>The goal is to be genuinely useful at 2am the night before an exam. That's the whole pitch.</P>
    </>
  );
}

function Contact() {
  return (
    <>
      <P>Questions, bug reports, wrong answers, privacy requests, or anything else — we want to hear it, especially correctness bugs.</P>
      <P><strong>Email:</strong> suggestions@nerdsuite.app</P>
      <P style={{ color: "#7C8099", fontSize: 13 }}>A real contact method is here both because it's the right thing for a tool people rely on, and because "no way to reach anyone" is a trust and search-quality signal we'd rather not send.</P>
    </>
  );
}

const VIEW = { privacy: Privacy, terms: Terms, accuracy: Accuracy, about: About, contact: Contact };

export default function Legal() {
  const [tab, setTab] = useState("privacy");
  const Body = VIEW[tab];
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", color: "#E8E9F3", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <main style={{ maxWidth: "min(720px, 92vw)", margin: "0 auto", padding: "clamp(40px,6vw,72px) clamp(20px,4vw,36px) 64px" }}>
        <Link to="/" style={{ fontSize: 13, color: "#7C8099", textDecoration: "none" }}>← NerdSuite</Link>
        <h1 style={{ fontSize: "clamp(24px,3.5vw,32px)", fontWeight: 800, margin: "18px 0 4px" }}>The boring but necessary</h1>
        <p style={{ fontSize: 13, color: "#5B5E7A", margin: "0 0 24px" }}>Last updated {UPDATED}.</p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 24 }}>
          {TABS.map((t) => {
            const on = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  fontSize: 13, fontWeight: 600, padding: "8px 15px", borderRadius: 9,
                  border: `1px solid ${on ? "#7C3AED" : "#1C1E2E"}`,
                  background: on ? "#7C3AED22" : "transparent",
                  color: on ? "#9A6BFF" : "#9A9DBE", cursor: "pointer",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <Body />
      </main>
    </div>
  );
}
